import api from '@/lib/axios';

export interface PromotionRule {
  id?: string;
  ruleType: 'PRODUCT' | 'CATEGORY' | 'BRAND' | 'ANY' | 'COMBO_ITEM';
  productId?: string;
  productName?: string;
  categoryId?: string;
  brandId?: string;
  requiredQuantity?: number;
  freeProductId?: string;
  freeQuantity?: number;
  comboPrice?: number;
  maxComboUnits?: number;
  discountPercent?: number;
  discountAmount?: number;
}

export interface PromotionBranch {
  branchId: string;
  branch?: { id: string; name: string };
}

export interface Promotion {
  id: string;
  companyId: string;
  name: string;
  description?: string;
  type: 'PERCENTAGE' | 'FIXED' | 'BUY_X_GET_Y' | 'COMBO' | 'VOLUME';
  status: 'ACTIVE' | 'INACTIVE';
  discountPercent?: number;
  discountAmount?: number;
  startDate?: string;
  endDate?: string;
  priority: number;
  maxUses?: number;
  usedCount: number;
  minPurchaseAmount?: number;
  applyToAllBranches: boolean;
  rules: PromotionRule[];
  branches: PromotionBranch[];
  createdAt: string;
  updatedAt: string;
}

export interface CreatePromotionDto {
  companyId: string;
  name: string;
  description?: string;
  type: string;
  status?: string;
  discountPercent?: number;
  discountAmount?: number;
  startDate?: string;
  endDate?: string;
  priority?: number;
  maxUses?: number;
  minPurchaseAmount?: number;
  applyToAllBranches?: boolean;
  rules?: Omit<PromotionRule, 'id'>[];
  branches?: { branchId: string }[];
}

export interface CalculatePromotionItem {
  productId: string;
  quantity: number;
  unitPrice: number;
  categoryId?: string;
  brandId?: string;
}

export interface AppliedPromotion {
  promotionId: string;
  promotionName: string;
  promotionType: string;
  description: string;
  discountAmount: number;
  affectedItems: string[];
}

export interface CalculatePromotionResult {
  appliedPromotions: AppliedPromotion[];
  itemDiscounts: Record<string, number>;
  totalDiscount: number;
  updatedItems: CalculatePromotionItem[];
}

export const promotionsService = {

  async recordUsage(promotionIds: string[]) {
    const response = await api.post('/promotions/record-usage', { promotionIds });
    return response.data;
  },

  async findAll(params: {
    companyId: string;
    status?: string;
    type?: string;
    branchId?: string;
    active?: boolean;
    page?: number;
    limit?: number;
  }) {
    const response = await api.get('/promotions', { params });
    return response.data as {
      data: Promotion[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  },

  async findById(id: string) {
    const response = await api.get(`/promotions/${id}`);
    return response.data as Promotion;
  },

  async create(dto: CreatePromotionDto) {
    const response = await api.post('/promotions', dto);
    return response.data as Promotion;
  },

  async update(id: string, dto: Partial<CreatePromotionDto>) {
    const response = await api.put(`/promotions/${id}`, dto);
    return response.data as Promotion;
  },

  async deactivate(id: string) {
    const response = await api.delete(`/promotions/${id}`);
    return response.data as Promotion;
  },

  async getActiveProductIds(companyId: string, branchId?: string) {
    const response = await api.get('/promotions/active-products', {
      params: { companyId, branchId },
    });
    return response.data as {
      productIds: string[];
      productPromotions: Record<string, {
        promotionId: string;
        promotionName: string;
        promotionType: string;
        description: string;
      }[]>;
    };
  },

  async calculate(dto: {
    companyId: string;
    branchId?: string;
    customerId?: string;
    items: CalculatePromotionItem[];
  }) {
    const response = await api.post('/promotions/calculate', dto);
    return response.data as CalculatePromotionResult;
  },
};
